import { pluralRu } from './pluralRu'

/**
 * SNAKE-18: pure formatting helpers for ProfileScreen. Lives in its own module
 * so unit tests can import it without dragging in DOM/window dependencies via
 * NetworkManager.
 *
 * All helpers handle null/undefined and zero inputs because the GDD "new
 * player" edge case expects "—" placeholders where a record has not yet been
 * set.
 */

export interface ProfileSummaryShape {
  username?: string | null
  firstName?: string | null
  createdAt?: string
  gamesPlayed?: number
  evacuationRate?: number
}

const MAX_NAME_LENGTH = 20

export function resolvePlayerName(stats: ProfileSummaryShape): string {
  const raw = pickNonEmpty(stats.username) ?? pickNonEmpty(stats.firstName) ?? 'Player'
  if (raw.length <= MAX_NAME_LENGTH) return raw
  return `${raw.slice(0, MAX_NAME_LENGTH - 1)}…`
}

function pickNonEmpty(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

export function formatRegistrationDate(value: string | undefined): string {
  if (!value) return '—'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return '—'
  try {
    return new Intl.DateTimeFormat('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(parsed)
  } catch {
    return parsed.toISOString().slice(0, 10)
  }
}

export function formatEvacRate(stats: ProfileSummaryShape): string {
  if (safeInt(stats.gamesPlayed) === 0) return 'Сыграй первую игру'
  const rate = typeof stats.evacuationRate === 'number' && Number.isFinite(stats.evacuationRate)
    ? stats.evacuationRate
    : 0
  return `${Math.max(0, Math.round(rate * 100))}%`
}

export function formatNumberOrDash(value: unknown): string {
  const n = safeInt(value)
  return n > 0 ? String(n) : '—'
}

export function formatStarsOrDash(value: unknown): string {
  const n = safeInt(value)
  return n > 0 ? `${n} ⭐` : '—'
}

export function formatStreakOrDash(value: unknown): string {
  const n = safeInt(value)
  return n > 0 ? `${n} ${pluralDay(n)}` : '—'
}

export function formatBestDailyRank(value: unknown): string {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return '—'
  return `#${Math.floor(value)}`
}

export function pluralDay(n: number): string {
  return pluralRu(n, 'день', 'дня', 'дней')
}

export function safeInt(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 0
  return Math.max(0, Math.floor(value))
}
